import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Award, Lock } from 'lucide-react'
import { achievements, Achievement, getTierBg } from '../data/achievements'
import { loginPlayer, type PlayerData } from '../utils/api'

export default function Achievements() {
  const [player, setPlayer] = useState<PlayerData | null>(null)
  const [filter, setFilter] = useState<string>('all')

  useEffect(() => {
    const currentPw = localStorage.getItem('hsk2-current-player')
    if (currentPw) {
      loginPlayer(currentPw).then(setPlayer).catch(() => {})
    }
  }, [])

  const unlockedIds = new Set(player?.achievements.map(a => a.id) || [])
  const unlockedCount = unlockedIds.size
  const totalCount = achievements.length

  const categoryFilters = [
    { id: 'all', label: 'All', icon: '🏆' },
    { id: 'score', label: 'Score', icon: '⭐' },
    { id: 'streak', label: 'Streak', icon: '🔥' },
    { id: 'words', label: 'Words', icon: '📚' },
    { id: 'games', label: 'Games', icon: '🎮' },
    { id: 'special', label: 'Special', icon: '✨' },
  ]

  const filtered = filter === 'all'
    ? achievements
    : achievements.filter(a => a.category === filter)

  const getUnlockDate = (id: string): string | null => {
    const found = player?.achievements.find(a => a.id === id)
    return found ? new Date(found.unlockedAt).toLocaleDateString() : null
  }

  const tierOrder: Record<Achievement['tier'], number> = { bronze: 0, silver: 1, gold: 2, diamond: 3 }

  const sorted = [...filtered].sort((a, b) => {
    const aUnlocked = unlockedIds.has(a.id) ? 0 : 1
    const bUnlocked = unlockedIds.has(b.id) ? 0 : 1
    if (aUnlocked !== bUnlocked) return aUnlocked - bUnlocked
    return tierOrder[a.tier] - tierOrder[b.tier]
  })

  return (
    <div className="page-shell bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-3 md:p-4 flex flex-col">
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 gap-2">
          <Link
            to="/"
            className="bg-white/20 rounded-2xl p-2.5 md:p-3 active:scale-95 transition-transform"
          >
            <ArrowLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </Link>

          <div className="flex items-center gap-2 min-w-0">
            <Award className="w-7 h-7 md:w-10 md:h-10 text-yellow-300 shrink-0" />
            <h1 className="text-xl md:text-3xl font-bold text-white truncate">Achievements</h1>
          </div>

          <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:px-4 md:py-2 shrink-0">
            <span className="text-yellow-300 font-bold text-base md:text-lg">{unlockedCount}/{totalCount}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-3 mb-4">
          <div className="flex justify-between mb-1">
            <span className="text-white/80 text-sm">Achievement Progress</span>
            <span className="text-yellow-300 font-bold text-sm">
              {Math.round((unlockedCount / totalCount) * 100)}%
            </span>
          </div>
          <div className="bg-white/20 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-yellow-400 to-amber-500 h-full rounded-full transition-all duration-500"
              style={{ width: `${(unlockedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>

        {/* Player Info */}
        {player ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-white font-bold text-base md:text-lg truncate">{player.name}</p>
              <p className="text-white/60 text-xs md:text-sm">Total: {player.totalScore} pts</p>
            </div>
            <div className="flex gap-3 md:gap-4 text-center w-full sm:w-auto justify-between sm:justify-start">
              <div>
                <p className="text-white font-bold">{player.wordsLearned.length}</p>
                <p className="text-white/60 text-xs">Words</p>
              </div>
              <div>
                <p className="text-white font-bold">{player.bestStreak}</p>
                <p className="text-white/60 text-xs">Streak</p>
              </div>
              <div>
                <p className="text-white font-bold">{player.sessionsPlayed}</p>
                <p className="text-white/60 text-xs">Games</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-3 mb-4 text-center">
            <p className="text-white/80">Save a score on the Leaderboard to start tracking achievements!</p>
          </div>
        )}

        {/* Category Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {categoryFilters.map(cat => (
            <button
              key={cat.id}
              onClick={() => setFilter(cat.id)}
              className={`flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-bold whitespace-nowrap
                         active:scale-95 transition-all ${
                filter === cat.id
                  ? 'bg-white text-purple-700'
                  : 'bg-white/20 text-white'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Achievements Grid */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {sorted.map((achievement) => {
              const unlocked = unlockedIds.has(achievement.id)
              const unlockDate = getUnlockDate(achievement.id)

              return (
                <div
                  key={achievement.id}
                  className={`rounded-2xl p-4 border-2 transition-all ${
                    unlocked
                      ? `${getTierBg(achievement.tier)} shadow-lg`
                      : 'bg-gray-800/40 border-gray-600/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className={`text-3xl ${unlocked ? '' : 'grayscale opacity-40'}`}>
                      {achievement.icon}
                    </span>
                    {!unlocked && <Lock className="w-4 h-4 text-gray-400" />}
                  </div>
                  <h3 className={`font-bold text-sm ${unlocked ? 'text-gray-800' : 'text-gray-400'}`}>
                    {achievement.title}
                  </h3>
                  <p className={`text-xs ${unlocked ? 'text-gray-600' : 'text-gray-500'}`}>
                    {achievement.titleCn}
                  </p>
                  <p className={`text-xs mt-1 ${unlocked ? 'text-gray-500' : 'text-gray-500'}`}>
                    {achievement.description}
                  </p>
                  {unlockDate && (
                    <p className="text-xs text-green-600 mt-1 font-medium">
                      Unlocked {unlockDate}
                    </p>
                  )}
                  <div className="mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold capitalize ${
                      achievement.tier === 'bronze' ? 'bg-amber-200 text-amber-800' :
                      achievement.tier === 'silver' ? 'bg-gray-200 text-gray-700' :
                      achievement.tier === 'gold' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-cyan-200 text-cyan-800'
                    }`}>
                      {achievement.tier}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
